import { useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import "react-tabs/style/react-tabs.css";
import { Icon } from "semantic-ui-react";
import {
  Fetchexecutequery,
  Fetchupdatequery,
} from "../../../services/process/BRS1/brsService7";
import { CreateData } from "../../../services/process/process_data/process_data";

// ── Helpers ───────────────────────────────────────────────────
const fmt   = n => Number(n||0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const parse = s => parseFloat(String(s).replace(/[,\s₹]/g, "")) || 0;

let _uid = 1000;
const nextId = () => ++_uid;

const getFormattedDate = () => new Date().toISOString().slice(0, 19).replace("T", " ");

function makeNode(name = "New category") {
  return { _localId: nextId(), id: null, name, amount: 0, fromDate: "", toDate: "", open: true, children: [] };
}
function makeSub() {
  return { _localId: nextId(), id: null, name: "", amount: 0, expCode: "", costCode: "", fromDate: "", toDate: "", children: [] };
}
function updateNode(nodes, lid, fn) {
  return nodes.map(n => {
    if (n._localId === lid) return { ...n, ...fn(n) };
    if (n.children?.length) return { ...n, children: updateNode(n.children, lid, fn) };
    return n;
  });
}
function deleteNode(nodes, lid) {
  return nodes.filter(n => n._localId !== lid).map(n => ({ ...n, children: n.children ? deleteNode(n.children, lid) : [] }));
}
function addChild(nodes, plid) {
  return nodes.map(n => {
    if (n._localId === plid) return { ...n, open: true, children: [...n.children, makeSub()] };
    if (n.children?.length) return { ...n, children: addChild(n.children, plid) };
    return n;
  });
}
function addSibling(nodes, slid) {
  const r = [];
  for (const n of nodes) {
    r.push({ ...n, children: n.children ? addSibling(n.children, slid) : [] });
    if (n._localId === slid) r.push(makeSub());
  }
  return r;
}
function sumChildren(nodes) { return nodes.reduce((s, n) => s + (n.amount || 0), 0); }

const FY_OPTS = ["FY 2023–24","FY 2024–25","FY 2025–26","FY 2026–27"];

// ── Financial Year date range helper ──────────────────────────
// Returns { fyStart: "YYYY-MM-DD", fyEnd: "YYYY-MM-DD" }
// Handles "FY 2025–26" (en-dash) and "FY 2025-26" (hyphen)
function getFYDateRange(fy) {
  const match = fy.match(/(\d{4})/);
  if (!match) return null;
  const startYear = parseInt(match[1]);
  return {
    fyStart: `${startYear}-04-01`,
    fyEnd:   `${startYear + 1}-03-31`,
  };
}

// ── Design tokens ─────────────────────────────────────────────
const FONT       = "'Segoe UI', system-ui, sans-serif";
const NAVY       = "#1E3A5F";
const BORDER     = "#d0d5dd";
const BG_INPUT   = "#f0f2f5";
const BG_PAGE    = "#e8eaf0";
const BG_HDR_COL = "#e8eaee";
const TEXT_MAIN  = "#222";
const TEXT_LABEL = NAVY;
const TEXT_META  = "#666";
const RED        = "#c0392b";
const GREEN      = "#1a7a4a";

// ── Uniform sizing ────────────────────────────────────────────
const INPUT_H = 34;
const ROW_PY  = "7px";
const ROW_PX  = "12px";
const INDENT  = "48px";
const F_BASE  = 13;
const F_SM    = 12;
const F_HDR   = 14;
const F_COL   = 12;

const selectArrow = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 10 10'%3E%3Cpath fill='%23666' d='M5 7L0 2h10z'/%3E%3C/svg%3E")`;

// ── Base styles ───────────────────────────────────────────────
const inputBase = {
  fontFamily: FONT, fontSize: F_BASE, color: TEXT_MAIN,
  background: BG_INPUT, border: `1px solid ${BORDER}`,
  borderRadius: 4, height: INPUT_H, width: "100%",
  padding: "0 9px", outline: "none", boxSizing: "border-box",
};

const selectBase = {
  ...inputBase,
  appearance: "none", WebkitAppearance: "none", cursor: "pointer",
  backgroundImage: selectArrow, backgroundRepeat: "no-repeat",
  backgroundPosition: "right 8px center", paddingRight: 26,
};

const colHdr = {
  fontFamily: FONT, fontSize: F_COL, fontWeight: 700,
  color: "#333", textTransform: "uppercase", letterSpacing: "0.05em",
  display: "flex", alignItems: "center",
};

const CAT_COLS = "28px 200px 130px 130px 160px 70px";
const SUB_COLS = "200px 130px 130px 130px 130px 160px 70px";

// ── Reusable components ───────────────────────────────────────
function FieldLabel({ children }) {
  return (
    <div style={{ fontFamily: FONT, fontSize: F_BASE, fontWeight: 600, color: TEXT_LABEL, marginBottom: 5 }}>
      {children}:
    </div>
  );
}

function SelectField({ label, value, onChange, options, loading }) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <select value={value} onChange={e => onChange(e.target.value)} disabled={loading} style={selectBase}>
        <option value="">{loading ? "Loading…" : `Select ${label.toLowerCase()}`}</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function InlineSelect({ value, onChange, options, loading, placeholder }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} disabled={loading} style={selectBase}>
      <option value="">{loading ? "Loading…" : (placeholder || "Select…")}</option>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function AmtInput({ value, bold, color, red, onChange }) {
  const [editing, setEditing] = useState(false);
  const [raw, setRaw]         = useState("");

  const commit = () => { setEditing(false); onChange(parse(raw)); };

  return (
    <div style={{ position: "relative" }}>
      <span style={{
        position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)",
        fontSize: F_BASE, color: TEXT_META, pointerEvents: "none",
      }}>₹</span>
      <input
        type="text"
        value={editing ? raw : fmt(value)}
        onFocus={() => { setEditing(true); setRaw(value || ""); }}
        onChange={e => setRaw(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); e.target.blur(); } }}
        style={{
          ...inputBase,
          paddingLeft: 22, paddingRight: 6,
          fontWeight: bold ? 600 : 400,
          color: red ? RED : (color || TEXT_MAIN),
          fontVariantNumeric: "tabular-nums",
          textAlign: "right",
        }}
      />
    </div>
  );
}

function ActionBtn({ onClick, title, danger, children }) {
  return (
    <button title={title} onClick={onClick} style={{
      width: 30, height: INPUT_H, borderRadius: 4, padding: 0, flexShrink: 0,
      border: `1px solid ${danger ? "#f5c6c6" : BORDER}`,
      background: danger ? "#fdf0f0" : "#f5f5f5",
      cursor: "pointer", fontSize: 14,
      color: danger ? RED : NAVY,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: FONT,
    }}>{children}</button>
  );
}

// ── Sub row ───────────────────────────────────────────────────
// parentFromDate / parentToDate clamp the date pickers to the
// category's chosen range (falling back to the FY bounds).
function SubRow({ node, parentAmount, parentFromDate, parentToDate, expCodeOpts, costCodeOpts, loadingCodes, onChange, onDelete, onAddSibling }) {
  const [hov, setHov] = useState(false);
  const over = node.amount > parentAmount && parentAmount > 0;

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "grid", gridTemplateColumns: SUB_COLS, gap: 6,
        alignItems: "center",
        padding: `${ROW_PY} ${ROW_PX} ${ROW_PY} ${INDENT}`,
        borderTop: `1px solid ${BORDER}`,
        background: hov ? "#f0f4fb" : "#fff",
        transition: "background 0.12s",
      }}
    >
      <input
        value={node.name}
        onChange={e => onChange(node._localId, n => ({...n, name: e.target.value}))}
        style={inputBase}
        placeholder="Sub-category name"
      />
      <InlineSelect
        value={node.expCode || ""}
        onChange={v => onChange(node._localId, n => ({...n, expCode: v}))}
        options={expCodeOpts} loading={loadingCodes} placeholder="Exp. Code"
      />
      <InlineSelect
        value={node.costCode || ""}
        onChange={v => onChange(node._localId, n => ({...n, costCode: v}))}
        options={costCodeOpts} loading={loadingCodes} placeholder="Cost Code"
      />
      {/* FIX: min/max now correctly bound to parent category's date range */}
      <input
        type="date"
        value={node.fromDate || ""}
        onChange={e => onChange(node._localId, n => ({...n, fromDate: e.target.value}))}
        style={inputBase}
        min={parentFromDate}
        max={parentToDate}
      />
      <input
        type="date"
        value={node.toDate || ""}
        onChange={e => onChange(node._localId, n => ({...n, toDate: e.target.value}))}
        style={inputBase}
        min={parentFromDate}
        max={parentToDate}
      />
      <AmtInput value={node.amount} red={over} onChange={v => onChange(node._localId, n => ({...n, amount: v}))} />
      <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
        <ActionBtn title="Add row below" onClick={() => onAddSibling(node._localId)}>+</ActionBtn>
        <ActionBtn title="Delete"        onClick={() => onDelete(node._localId)} danger>×</ActionBtn>
      </div>
    </div>
  );
}

// ── Category row ──────────────────────────────────────────────
// fyStart / fyEnd clamp the category-level date pickers to the
// selected financial year.
function CategoryRow({ node, totalBudget, catIndex, fyStart, fyEnd, expCodeOpts, costCodeOpts, loadingCodes, onChange, onDelete, onAddChild, onAddSibling }) {
  const childSum  = sumChildren(node.children);
  const childOver = node.children.length > 0 && childSum > node.amount;
  const topPct    = totalBudget > 0 ? Math.round((node.amount / totalBudget) * 100) : 0;

  // Effective bounds passed down to sub-rows:
  // if the category has its own dates use those, otherwise fall back to the FY.
  const subFromDate = node.fromDate || fyStart;
  const subToDate   = node.toDate   || fyEnd;

  return (
    <div style={{ borderTop: catIndex > 0 ? `1px solid ${BORDER}` : "none" }}>

      {/* Category data row */}
      <div style={{
        display: "grid", gridTemplateColumns: CAT_COLS, gap: 6,
        alignItems: "center", padding: `${ROW_PY} ${ROW_PX}`,
        borderLeft: `3px solid ${NAVY}`, background: "#fff",
      }}>
        <button
          onClick={() => onChange(node._localId, n => ({...n, open: !n.open}))}
          style={{
            width: 24, height: 24, borderRadius: 3,
            border: `1px solid ${BORDER}`, background: "#fff",
            cursor: "pointer", fontSize: 9, color: NAVY, fontWeight: 700,
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 0, flexShrink: 0, fontFamily: FONT,
          }}
        >{node.open ? "▲" : "▼"}</button>

        <input
          value={node.name}
          onChange={e => onChange(node._localId, n => ({...n, name: e.target.value}))}
          style={{ ...inputBase, fontWeight: 600 }}
          placeholder="Category name"
        />

        {/* FIX: min/max now bound to the FY date range */}
        <input
          type="date"
          value={node.fromDate || ""}
          onChange={e => onChange(node._localId, n => ({...n, fromDate: e.target.value}))}
          style={inputBase}
          min={fyStart}
          max={fyEnd}
        />
        <input
          type="date"
          value={node.toDate || ""}
          onChange={e => onChange(node._localId, n => ({...n, toDate: e.target.value}))}
          style={inputBase}
          min={fyStart}
          max={fyEnd}
        />

        <AmtInput value={node.amount} bold color={NAVY} onChange={v => onChange(node._localId, n => ({...n, amount: v}))} />
        <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
          <ActionBtn title="Delete" onClick={() => onDelete(node._localId)} danger>X</ActionBtn>
        </div>
      </div>

      {/* Sub-categories */}
      {node.open && (
        <>
          {/* Sub column header row */}
          {node.children.length > 0 && (
            <div style={{
              display: "grid", gridTemplateColumns: SUB_COLS, gap: 6,
              padding: `8px ${ROW_PX} 8px ${INDENT}`,
              background: BG_HDR_COL,
              borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`,
            }}>
              {["Sub-category", "Exp. Code", "Cost Code", "From", "To", "Amount", ""].map((h, i) => (
                <div key={i} style={colHdr}>{h}</div>
              ))}
            </div>
          )}

          {node.children.length === 0 && (
            <div style={{ fontFamily: FONT, fontSize: F_BASE, color: TEXT_META, fontStyle: "italic", padding: `8px ${ROW_PX} 8px ${INDENT}`, background: "#fff" }}>
              No sub-categories yet.
            </div>
          )}

          {node.children.map(child => (
            <SubRow
              key={child._localId}
              node={child}
              parentAmount={node.amount}
              // FIX: pass effective date bounds so sub-row pickers are clamped
              parentFromDate={subFromDate}
              parentToDate={subToDate}
              expCodeOpts={expCodeOpts}
              costCodeOpts={costCodeOpts}
              loadingCodes={loadingCodes}
              onChange={onChange}
              onDelete={onDelete}
              onAddSibling={onAddSibling}
            />
          ))}

          <div style={{ padding: `6px ${ROW_PX} 8px ${INDENT}`, background: "#fff", borderTop: `1px solid ${BORDER}` }}>
            <button onClick={() => onAddChild(node._localId)} style={{
              background: "transparent", border: `1px dashed ${BORDER}`, borderRadius: 4,
              padding: "4px 14px", fontFamily: FONT, fontSize: F_BASE, fontWeight: 500,
              color: NAVY, cursor: "pointer",
            }}>+ Add sub-category</button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Budget Form (Tab 1) ───────────────────────────────────────
function BudgetForm({ editId, onSaveSuccess }) {
  const [userCredentials] = useState(JSON.parse(sessionStorage.getItem("userCredentials")));

  const [fy,          setFy]          = useState("FY 2025–26");
  const [entity,      setEntity]      = useState("");
  const [divisions,   setDivisions]   = useState("");
  const [totalBudget, setTotalBudget] = useState(0);
  const [nodes,       setNodes]       = useState([]);
  const [saving,      setSaving]      = useState(false);

  const [entityOpts,    setEntityOpts]    = useState([]);
  const [divisionsOpts, setDivisionsOpts] = useState([]);
  const [expCodeOpts,   setExpCodeOpts]   = useState([]);
  const [costCodeOpts,  setCostCodeOpts]  = useState([]);
  const [loadingMeta,   setLoadingMeta]   = useState(true);
  const [loadingCodes,  setLoadingCodes]  = useState(true);

  useEffect(() => {
    const init = async () => {
      try {
        const creds = Fetchexecutequery();
        const run   = async (q) => { const r = await axios.post(creds[1], { query: q }, creds[0]); return r.data || []; };

        const [entities, divisions, expCodes, costCodes] = await Promise.all([
          run(`SELECT * FROM user_management.entities`),
          run(`SELECT * FROM user_management.divisions`),
          run(`SELECT * FROM payments.expense_categories WHERE is_Active = 1`),
          run(`SELECT * FROM payments.cost_center_details WHERE is_Active = 1`),
        ]);

        setEntityOpts(entities.map(r     => ({ value: r.entity_id,    label: r.name })));
        setDivisionsOpts(divisions.map(r => ({ value: r.division_id,  label: r.name })));
        setExpCodeOpts(expCodes.map(r    => ({ value: r.id,           label: r.expense_code })));
        setCostCodeOpts(costCodes.map(r  => ({ value: r.id,           label: r.cost_center })));
        setLoadingMeta(false);
        setLoadingCodes(false);
        console.log("expCodes sample:", expCodes[0]);
        console.log("costCodes sample:", costCodes[0]);

        if (editId) {
          const [headers, cats, subs] = await Promise.all([
            run(`SELECT * FROM budget.budget_header WHERE id = ${editId}`),
            run(`SELECT * FROM budget.budget_category WHERE budget_header_id = ${editId} AND is_active = 1 ORDER BY sort_order`),
            run(`SELECT * FROM budget.budget_subcategory WHERE budget_header_id = ${editId} AND is_active = 1 ORDER BY sort_order`),
          ]);

          if (headers.length) {
            const h = headers[0];
            setFy(h.financial_year);
            setEntity(String(h.entity_id || ""));
            setDivisions(String(h.division_id || ""));
            setTotalBudget(Number(h.total_budget));
          }

          const builtNodes = cats.map(cat => ({
            _localId: nextId(), id: cat.id,
            name: cat.category_name,
            amount: Number(cat.amount),
            fromDate: cat.from_date ? cat.from_date.slice(0, 10) : "",
            toDate:   cat.to_date   ? cat.to_date.slice(0, 10)   : "",
            open: true,
            children: subs
              .filter(s => s.budget_category_id === cat.id)
              .map(s => ({
                _localId: nextId(), id: s.id,
                name: s.subcategory_name,
                amount: Number(s.amount),
                expCode:  String(s.expense_code_id || ""),
                costCode: String(s.cost_code_id    || ""),
                fromDate: s.from_date ? s.from_date.slice(0, 10) : "",
                toDate:   s.to_date   ? s.to_date.slice(0, 10)   : "",
                children: [],
              })),
          }));
          setNodes(builtNodes);
        }
      } catch (err) {
        console.error("Init error:", err);
        setLoadingMeta(false);
        setLoadingCodes(false);
      }
    };
    init();
  }, [editId]);

  const onChange     = useCallback((lid, fn) => setNodes(p => updateNode(p, lid, fn)), []);
  const onDelete     = useCallback((lid)     => setNodes(p => deleteNode(p, lid)),     []);
  const onAddChild   = useCallback((plid)    => setNodes(p => addChild(p, plid)),      []);
  const onAddSibling = useCallback((slid)    => setNodes(p => addSibling(p, slid)),    []);

  // FIX: derive FY bounds once here; used by CategoryRow (and transitively SubRow)
  const fyRange  = getFYDateRange(fy);
  const fyStart  = fyRange?.fyStart || "";
  const fyEnd    = fyRange?.fyEnd   || "";

  const topSum    = sumChildren(nodes);
  const unalloc   = totalBudget - topSum;
  const over      = topSum > totalBudget;
  const pctFill   = totalBudget > 0 ? Math.min((topSum / totalBudget) * 100, 100) : 0;
  const totalCats = nodes.length;
  const totalSubs = nodes.reduce((a, n) => a + n.children.length, 0);

  const handleSave = async () => {
    if (!entity) { alert("Please select an Entity."); return; }
    if (!fy)     { alert("Please select a Financial Year."); return; }

    // ── FY date range ────────────────────────────────────────
    if (!fyRange) { alert("Invalid Financial Year format."); return; }

    // ── Validate categories ──────────────────────────────────
    for (let ci = 0; ci < nodes.length; ci++) {
      const cat = nodes[ci];

      if (!cat.name.trim()) {
        alert(`Category ${ci + 1}: Name is required.`); return;
      }
      if (!cat.amount || cat.amount <= 0) {
        alert(`Category "${cat.name}": Budget amount must be greater than 0.`); return;
      }
      if (cat.fromDate && cat.toDate && cat.fromDate > cat.toDate) {
        alert(`Category "${cat.name}": From date cannot be after To date.`); return;
      }
      if (cat.fromDate && (cat.fromDate < fyStart || cat.fromDate > fyEnd)) {
        alert(`Category "${cat.name}": From date must be within ${fyStart} to ${fyEnd}.`); return;
      }
      if (cat.toDate && (cat.toDate < fyStart || cat.toDate > fyEnd)) {
        alert(`Category "${cat.name}": To date must be within ${fyStart} to ${fyEnd}.`); return;
      }

      // ── Validate sub-categories ────────────────────────────
      const catFrom = cat.fromDate || fyStart;
      const catTo   = cat.toDate   || fyEnd;

      for (let si = 0; si < cat.children.length; si++) {
        const sub = cat.children[si];

        if (!sub.name.trim()) {
          alert(`Category "${cat.name}" › Sub-category ${si + 1}: Name is required.`); return;
        }
        if (!sub.amount || sub.amount <= 0) {
          alert(`Category "${cat.name}" › Sub "${sub.name}": Amount must be greater than 0.`); return;
        }
        if (sub.fromDate && sub.toDate && sub.fromDate > sub.toDate) {
          alert(`Category "${cat.name}" › Sub "${sub.name}": From date cannot be after To date.`); return;
        }
        if (sub.fromDate && (sub.fromDate < catFrom || sub.fromDate > catTo)) {
          alert(`Category "${cat.name}" › Sub "${sub.name}": From date must be within category range (${catFrom} to ${catTo}).`); return;
        }
        if (sub.toDate && (sub.toDate < catFrom || sub.toDate > catTo)) {
          alert(`Category "${cat.name}" › Sub "${sub.name}": To date must be within category range (${catFrom} to ${catTo}).`); return;
        }
      }
    }

    setSaving(true);
    const now = getFormattedDate();

    try {
      let headerId = editId;

      if (!editId) {
        // ── Duplicate header check: same FY + entity + division ─
        const dupCreds = Fetchexecutequery();
        const dupRes   = await axios.post(dupCreds[1], {
          query: `
            SELECT id FROM budget.budget_header
            WHERE financial_year = '${fy}'
              AND entity_id = ${entity}
              AND ${divisions ? `division_id = ${divisions}` : `(division_id IS NULL OR division_id = '')`}
              AND is_active = 1
            LIMIT 1
          `,
        }, dupCreds[0]);

        if ((dupRes.data || []).length > 0) {
          alert("A budget already exists for this Financial Year, Entity and Division. Please edit the existing record instead.");
          setSaving(false);
          return;
        }

        const createCreds = CreateData();
        await axios.post(createCreds[1], {
          database: "budget", table: "budget_header",
          formData: {
            financial_year: fy, entity_id: entity, division_id: divisions || null,
            total_budget: totalBudget, status: "draft", is_active: 1,
            created_by: userCredentials.user_id, modified_by: userCredentials.user_id,
            created_on: now, last_updated_on: now, last_updated_by: userCredentials.user_name,
          },
        }, createCreds[0]);

        const fetchHeaderCreds = Fetchexecutequery();
        const fetchHeaderRes   = await axios.post(fetchHeaderCreds[1], {
          query: `
            SELECT id FROM budget.budget_header
            WHERE financial_year = '${fy}'
              AND entity_id = ${entity}
              AND created_by = ${userCredentials.user_id}
              AND is_active = 1
            ORDER BY id DESC
            LIMIT 1
          `,
        }, fetchHeaderCreds[0]);

        headerId = (fetchHeaderRes.data || [])[0]?.id;
        if (!headerId) throw new Error("Could not retrieve new budget_header ID after insert");

      } else {
        // ── Duplicate check on edit — exclude current record ──
        const dupCreds = Fetchexecutequery();
        const dupRes   = await axios.post(dupCreds[1], {
          query: `
            SELECT id FROM budget.budget_header
            WHERE financial_year = '${fy}'
              AND entity_id = ${entity}
              AND ${divisions ? `division_id = ${divisions}` : `(division_id IS NULL OR division_id = '')`}
              AND is_active = 1
              AND id != ${editId}
            LIMIT 1
          `,
        }, dupCreds[0]);

        if ((dupRes.data || []).length > 0) {
          alert("Another budget already exists for this Financial Year, Entity and Division.");
          setSaving(false);
          return;
        }

        const uc = Fetchupdatequery();
        await axios.post(uc[1], {
          schema_name: "budget", table: "budget_header",
          set_column: {
            financial_year: fy, entity_id: entity, division_id: divisions || null,
            total_budget: totalBudget, modified_by: userCredentials.user_id,
            last_updated_on: now, last_updated_by: userCredentials.user_name,
          },
          where: [{ id: editId }],
        }, uc[0]);

        const uc2 = Fetchupdatequery();
        await axios.post(uc2[1], {
          schema_name: "budget", table: "budget_category",
          set_column: { is_active: 0 }, where: [{ budget_header_id: editId }],
        }, uc2[0]);

        const uc3 = Fetchupdatequery();
        await axios.post(uc3[1], {
          schema_name: "budget", table: "budget_subcategory",
          set_column: { is_active: 0 }, where: [{ budget_header_id: editId }],
        }, uc3[0]);
      }

      const cc = CreateData();
      for (let ci = 0; ci < nodes.length; ci++) {
        const cat = nodes[ci];

        await axios.post(cc[1], {
          database: "budget", table: "budget_category",
          formData: {
            budget_header_id: headerId, category_name: cat.name,
            from_date: cat.fromDate || null, to_date: cat.toDate || null,
            amount: cat.amount, sort_order: ci, is_active: 1,
            created_by: userCredentials.user_id, modified_by: userCredentials.user_id,
            created_on: now, last_updated_on: now, last_updated_by: userCredentials.user_name,
          },
        }, cc[0]);

        const fetchCatCreds = Fetchexecutequery();
        const fetchCatRes   = await axios.post(fetchCatCreds[1], {
          query: `
            SELECT id FROM budget.budget_category
            WHERE budget_header_id = ${headerId}
              AND category_name = '${cat.name.replace(/'/g, "\\'")}'
              AND sort_order = ${ci}
              AND is_active = 1
            ORDER BY id DESC
            LIMIT 1
          `,
        }, fetchCatCreds[0]);

        const catId = (fetchCatRes.data || [])[0]?.id;
        if (!catId) throw new Error(`Could not retrieve category ID for "${cat.name}"`);

        for (let si = 0; si < cat.children.length; si++) {
          const sub = cat.children[si];
          await axios.post(cc[1], {
            database: "budget", table: "budget_subcategory",
            formData: {
              budget_header_id: headerId, budget_category_id: catId,
              subcategory_name: sub.name,
              expense_code_id: sub.expCode  && sub.expCode  !== "" ? Number(sub.expCode)  : null,
              cost_code_id:    sub.costCode && sub.costCode !== "" ? Number(sub.costCode) : null,
              from_date: sub.fromDate || null, to_date: sub.toDate || null,
              amount: sub.amount, sort_order: si, is_active: 1,
              created_by: userCredentials.user_id, modified_by: userCredentials.user_id,
              created_on: now, last_updated_on: now, last_updated_by: userCredentials.user_name,
            },
          }, cc[0]);
        }
      }

      alert(editId ? "Budget updated successfully!" : "Budget created successfully!");
      onSaveSuccess();
    } catch (err) {
      console.error("Save error:", err);
      alert("Error saving budget. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // ── card + banner styles ───────────────────────────────────
  const card        = { background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 8, marginBottom: 14, overflow: "hidden" };
  const banner      = { padding: "10px 16px 8px", background: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BORDER}` };
  const bannerTitle = { fontFamily: FONT, fontSize: F_HDR, fontWeight: 700, color: NAVY };
  const bannerBadge = { fontFamily: FONT, fontSize: F_SM,  color: TEXT_META };

  return (
    <div style={{ fontFamily: FONT, background: BG_PAGE, color: TEXT_MAIN, fontSize: F_BASE, padding: "0 16px 40px" }}>

      {/* ── Budget Information ── */}
      <div style={{ ...card, marginTop: 12 }}>
        <div style={banner}><span style={bannerTitle}>Budget Information</span></div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 16, padding: "14px 16px", borderBottom: `1px solid ${BORDER}` }}>
          <div>
            <SelectField label="Financial Year" value={fy} onChange={setFy} options={FY_OPTS.map(o => ({ value: o, label: o }))} />
          </div>
          <SelectField label="Entity"   value={entity}    onChange={setEntity}    options={entityOpts}    loading={loadingMeta} />
          <SelectField label="Division" value={divisions} onChange={setDivisions} options={divisionsOpts} loading={loadingMeta} />
          <div>
            <FieldLabel>Total Budget</FieldLabel>
            <AmtInput value={totalBudget} bold color={NAVY} onChange={setTotalBudget} />
          </div>
          {/* Allocated / Unallocated block */}
          <div style={{ padding: "8px 9px", background: "#f9f9f9", border: `1px solid ${BORDER}`, borderRadius: 4, fontSize: F_SM, display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontWeight: 600, color: TEXT_LABEL }}>Allocated:</span>
              <span style={{ fontWeight: 700, color: NAVY }}>₹{fmt(topSum)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontWeight: 600, color: TEXT_LABEL }}>Unallocated:</span>
              <span style={{ fontWeight: 700, color: over ? RED : GREEN }}>₹{fmt(Math.abs(unalloc))}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Categories ── */}
      <div style={card}>
        <div style={{ padding: "12px 16px", background: "#fff", borderBottom: `1px solid ${BORDER}` }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={bannerTitle}>Categories &amp; Sub-categories</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div>
              <div style={{ fontSize: F_SM, fontWeight: 600, color: TEXT_LABEL, marginBottom: 2 }}>Summary</div>
              <div style={{ fontSize: F_BASE, fontWeight: 700, color: NAVY }}>{totalCats} categories · {totalSubs} sub-items</div>
            </div>
            <div>
              <div style={{ fontSize: F_SM, fontWeight: 600, color: TEXT_LABEL, marginBottom: 2 }}>Budget Utilization</div>
              <div style={{ fontSize: F_BASE, fontWeight: 700, color: NAVY }}>₹{fmt(topSum)} allocated · {Math.round(pctFill)}% utilized</div>
            </div>
          </div>
        </div>

        {/* Category column header */}
        {nodes.length > 0 && (
          <div style={{
            display: "grid", gridTemplateColumns: CAT_COLS, gap: 6,
            padding: `8px ${ROW_PX}`, background: BG_HDR_COL, borderBottom: `1px solid ${BORDER}`,
          }}>
            <div />
            {["Category", "From", "To", "Budget", ""].map((h, i) => (
              <div key={i} style={colHdr}>{h}</div>
            ))}
          </div>
        )}

        {nodes.length === 0 && (
          <div style={{ padding: "28px 0", textAlign: "center", fontFamily: FONT, fontSize: F_BASE, color: TEXT_META }}>
            No categories yet. Click "+ Add Category" below.
          </div>
        )}

        {nodes.map((node, i) => (
          <CategoryRow
            key={node._localId}
            node={node}
            totalBudget={totalBudget}
            catIndex={i}
            // FIX: pass FY bounds down so category date pickers are clamped
            fyStart={fyStart}
            fyEnd={fyEnd}
            expCodeOpts={expCodeOpts}
            costCodeOpts={costCodeOpts}
            loadingCodes={loadingCodes}
            onChange={onChange}
            onDelete={onDelete}
            onAddChild={onAddChild}
            onAddSibling={onAddSibling}
          />
        ))}

        <div style={{ padding: `8px ${ROW_PX} 10px` }}>
          <button onClick={() => setNodes(p => [...p, makeNode()])} style={{
            width: "100%", padding: "7px 0", borderRadius: 4,
            border: `1.5px dashed ${BORDER}`, background: "#f7f8fa",
            fontFamily: FONT, fontSize: F_BASE, fontWeight: 600, color: NAVY, cursor: "pointer",
          }}>+ Add Category</button>
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 4 }}>
        <button onClick={handleSave} disabled={saving} style={{
          padding: "8px 32px", borderRadius: 5,
          background: saving ? "#999" : NAVY,
          color: "#fff", border: "none",
          fontFamily: FONT, fontSize: F_BASE, fontWeight: 600,
          cursor: saving ? "not-allowed" : "pointer",
        }}>
          {saving ? "Saving…" : editId ? "Update" : "Save"}
        </button>
      </div>
    </div>
  );
}

// ── Budget List (Tab 2) ───────────────────────────────────────
function BudgetList({ onEdit }) {
  const [userCredentials] = useState(JSON.parse(sessionStorage.getItem("userCredentials")));
  const [rowData,    setRowData]    = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);
  const [filter,     setFilter]     = useState("");
  const gridRef = useRef(null);

  const formatColumnName = k => k.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

  useEffect(() => {
    const fetchList = async () => {
      try {
        const creds = Fetchexecutequery();
        const res   = await axios.post(creds[1], {
          query: `
            SELECT
              bh.id,
              bh.financial_year,
              COALESCE(e.name, '')  AS entity_name,
              COALESCE(d.name, '')  AS division_name,
              bh.total_budget,
              bh.status,
              bh.created_on
            FROM budget.budget_header bh
            LEFT JOIN user_management.entities  e ON e.entity_id   = bh.entity_id
            LEFT JOIN user_management.divisions d ON d.division_id = bh.division_id
            WHERE bh.is_active = 1
              AND bh.entity_id = ${userCredentials.selectedentity}
            ORDER BY bh.id DESC
          `,
        }, creds[0]);

        const data = res.data || [];
        if (data.length > 0) {
          const cols = Object.keys(data[0])
            .filter(k => k !== "id")
            .map(k => ({ headerName: formatColumnName(k), field: k, sortable: true, filter: true, resizable: true }));

          cols.push({
            headerName: "Action", field: "action", width: 90,
            cellRendererFramework: params => (
              <Icon name="edit"
                style={{ cursor: "pointer", color: NAVY, fontSize: 16 }}
                onClick={() => onEdit(params.data.id)}
              />
            ),
          });

          setColumnDefs(cols);
          setRowData(data);
        }
      } catch (err) {
        console.error("Error fetching budget list:", err);
      }
    };
    fetchList();
  }, []);

  return (
    <div style={{ padding: "12px 16px", background: BG_PAGE, minHeight: "100%" }}>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
        <input
          type="text" placeholder="Search…" value={filter}
          onChange={e => { setFilter(e.target.value); gridRef.current?.api?.setQuickFilter(e.target.value); }}
          style={{ ...inputBase, width: 260 }}
        />
      </div>
      <div className="ag-theme-alpine" style={{ height: 480, width: "100%", borderRadius: 6, overflow: "hidden", border: `1px solid ${BORDER}` }}>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          pagination={true}
          paginationPageSize={10}
        />
      </div>
    </div>
  );
}

// ── Root ──────────────────────────────────────────────────────
export default function BudgetEntry() {
  const [activeTab, setActiveTab] = useState(0);
  const [editId,    setEditId]    = useState(null);

  const handleEdit        = (id) => { setEditId(id);  setActiveTab(0); };
  const handleNew         = ()   => { setEditId(null); setActiveTab(0); };
  const handleSaveSuccess = ()   => { setEditId(null); setActiveTab(1); };

  return (
    <div style={{ fontFamily: FONT }}>
      <h2 className="pt-1 pb-1 text-center" style={{ color: "#4969ab", fontSize: 18 }}>
        Budget Master
      </h2>

      <Tabs selectedIndex={activeTab} onSelect={setActiveTab}>
        <TabList>
          <Tab onClick={handleNew}>{editId ? "Edit Budget" : "Create Budget"}</Tab>
          <Tab>View Budgets</Tab>
        </TabList>

        <TabPanel>
          <BudgetForm editId={editId} onSaveSuccess={handleSaveSuccess} />
        </TabPanel>

        <TabPanel>
          <BudgetList onEdit={handleEdit} />
        </TabPanel>
      </Tabs>
    </div>
  );
}